import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Collapse, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { buildChart, buildKpi, columnFormat, formatValue, readThemeColors, tableColumns } from './dashboardTemplates';

const TILE_GRID = { kpi: { xs: 6, md: 3 }, table: { xs: 12 }, default: { xs: 12, md: 6 } };
const MAX_TABLE_ROWS = 50;

const queryText = (query) => (query?.language === 'mongo_pipeline'
  ? `${query.collection}: ${JSON.stringify(query.text, null, 2)}`
  : query?.text || '');

const KpiBody = ({ tile }) => {
  const kpi = buildKpi(tile);
  const trendColor = kpi.trend === 'up' ? 'var(--wb-success)' : kpi.trend === 'down' ? 'var(--wb-danger)' : 'var(--wb-text-faint)';
  return (
    <Box>
      <Typography sx={{ color: 'var(--wb-text-strong)', fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.2 }}>{kpi.value}</Typography>
      {kpi.variationText && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          {kpi.trend === 'up' ? <TrendingUpIcon fontSize="small" sx={{ color: trendColor }} />
            : kpi.trend === 'down' ? <TrendingDownIcon fontSize="small" sx={{ color: trendColor }} /> : null}
          <Typography variant="body2" sx={{ color: trendColor, fontWeight: 600 }}>{kpi.variationText}</Typography>
          {kpi.previous && <Typography variant="caption" sx={{ color: 'var(--wb-text-faint)' }}>vs {kpi.previous}</Typography>}
        </Stack>
      )}
    </Box>
  );
};

const TableBodyView = ({ tile }) => {
  const columns = tableColumns(tile);
  const rows = (tile.result.records || []).slice(0, MAX_TABLE_ROWS);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& .MuiTableCell-root': { color: 'var(--wb-text)', borderColor: 'var(--wb-border-16)', whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column} sx={{ color: 'var(--wb-text-faint) !important', fontWeight: 600 }}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={column}>{typeof row[column] === 'number' ? formatValue(row[column], columnFormat(tile, column)) : String(row[column] ?? '—')}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ChartBody = ({ tile, colors }) => {
  const chart = useMemo(() => buildChart(tile, colors), [tile, colors]);
  return <Chart options={chart.options} series={chart.series} type={chart.type} height={tile.type === 'donut' ? 260 : 240} />;
};

const notes = (result) => [
  result.truncated && 'Resultado recortado al máximo de filas.',
  result.display_truncated && 'Se muestran menos filas de las calculadas.'
].filter(Boolean);

export const DashboardTile = ({ tile, colors }) => {
  const [showQuery, setShowQuery] = useState(false);
  const result = tile.result || {};
  const empty = result.ok && !(result.records || []).length;
  return (
    <Box
      data-testid={`tile-${tile.id}`}
      sx={{ height: '100%', p: 1.5, borderRadius: 2, border: '1px solid var(--wb-border-20)', backgroundColor: 'var(--wb-overlay-08)', display: 'flex', flexDirection: 'column' }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', fontWeight: 600 }}>{tile.title}</Typography>
        <Chip size="small" label={tile.source?.label || tile.source?.engine} sx={{ height: 20, fontSize: '0.7rem', color: 'var(--wb-text-faint)', backgroundColor: 'var(--wb-overlay-08)' }} />
      </Stack>
      <Box sx={{ flexGrow: 1 }}>
        {!result.ok ? (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <WarningAmberIcon fontSize="small" sx={{ color: 'var(--wb-warning)', mt: '2px' }} />
            <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>
              No se pudo calcular{result.message ? `: ${result.message}` : '.'}
            </Typography>
          </Stack>
        ) : empty ? (
          <Typography variant="body2" sx={{ color: 'var(--wb-text-faint)' }}>Sin datos para este periodo.</Typography>
        ) : tile.type === 'kpi' ? <KpiBody tile={tile} />
          : tile.type === 'table' ? <TableBodyView tile={tile} />
            : <ChartBody tile={tile} colors={colors} />}
      </Box>
      {result.ok && notes(result).map((note) => (
        <Typography key={note} variant="caption" sx={{ color: 'var(--wb-text-faint)', display: 'block', mt: 0.5 }}>{note}</Typography>
      ))}
      <Button
        size="small"
        onClick={() => setShowQuery((value) => !value)}
        aria-expanded={showQuery}
        sx={{ alignSelf: 'flex-start', mt: 0.5, px: 0, minWidth: 0, textTransform: 'none', fontSize: '0.75rem', color: 'var(--wb-text-faint)' }}
      >
        {showQuery ? 'Ocultar consulta' : 'Ver consulta'}
      </Button>
      <Collapse in={showQuery} unmountOnExit>
        <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, borderRadius: 1, overflowX: 'auto', fontSize: '0.75rem', color: 'var(--wb-text)', backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border-16)' }}>
          {queryText(tile.query)}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Tablero del modo Dashboard (respuesta `dashboard` de agent-runtime o uno
 * guardado). En el chat recibe onSave/saving/savedId/onOpenSaved para
 * "Guardar tablero"; en "Mis tableros", hideHeader (la página ya muestra el
 * título).
 */
const DashboardView = ({ dashboard, hideHeader = false, onSave, saving = false, savedId = null, onOpenSaved, saveError = null }) => {
  const colors = useMemo(() => readThemeColors(), []);
  const tiles = dashboard.tiles || [];
  const failed = tiles.filter((tile) => !(tile.result || {}).ok).length;
  return (
    <Box sx={{ color: 'var(--wb-text-strong)' }}>
      {!hideHeader && (<>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          size="small"
          icon={<DashboardOutlinedIcon sx={{ color: 'var(--wb-accent) !important' }} />}
          label="Tablero"
          sx={{ backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)', fontWeight: 600 }}
        />
        {dashboard.period && <Typography variant="caption" sx={{ color: 'var(--wb-text-faint)' }}>{dashboard.period}</Typography>}
        {failed > 0 && (
          <Chip size="small" label={`${failed} con error`} sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)', fontWeight: 600 }} />
        )}
      </Stack>
      <Typography variant="h6" sx={{ color: 'var(--wb-text-strong)', fontWeight: 600 }}>{dashboard.title}</Typography>
      {dashboard.description && (
        <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', mb: 1.5 }}>{dashboard.description}</Typography>
      )}
      {(onSave || savedId) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
          {savedId ? (
            <>
              <Chip size="small" label="Guardado en Mis tableros" sx={{ backgroundColor: 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))', color: 'var(--wb-success)', fontWeight: 600 }} />
              {onOpenSaved && (
                <Button size="small" onClick={() => onOpenSaved(savedId)} sx={{ textTransform: 'none', color: 'var(--wb-accent)' }}>Abrir</Button>
              )}
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={onSave}
              disabled={saving}
              sx={{ textTransform: 'none', color: 'var(--wb-accent)', borderColor: 'var(--wb-accent-40)' }}
            >
              {saving ? 'Guardando…' : 'Guardar tablero'}
            </Button>
          )}
          {saveError && <Typography variant="caption" sx={{ color: 'var(--wb-danger)' }}>{saveError}</Typography>}
        </Stack>
      )}
      </>)}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        {tiles.map((tile) => {
          const size = TILE_GRID[tile.type] || TILE_GRID.default;
          return (
            <Grid item key={tile.id} {...size}>
              <DashboardTile tile={tile} colors={colors} />
            </Grid>
          );
        })}
      </Grid>
      {(dashboard.warnings || []).map((warning) => (
        <Stack key={warning} direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
          <WarningAmberIcon fontSize="small" sx={{ color: 'var(--wb-warning)', mt: '2px' }} />
          <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)' }}>{warning}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

export default DashboardView;
